import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { PageShell, ResearchReport } from '../components';
import { fetchRun } from '../lib/api';
import { useReportActions } from '../hooks/useReportActions';
import type { ResearchRunDetail, BriefingCategory } from '../types';
import { RUN_HISTORY_STATUS_LABELS } from '../constants/history';
import { glassStyle, fadeInAnimation } from '../styles';

const BRIEFING_CATEGORY_LABELS: Record<BriefingCategory, string> = {
  company: 'Empresa',
  industry: 'Setor',
  financial: 'Financeiro',
  news: 'Notícias',
};

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
}

interface BriefingItemProps {
  category: BriefingCategory;
  content: string;
}

const BriefingItem = ({ category, content }: BriefingItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white/80">
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 text-left font-['DM_Sans']"
      >
        <span className="text-sm font-medium text-gray-900">{BRIEFING_CATEGORY_LABELS[category]}</span>
        {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-600" /> : <ChevronDown className="h-4 w-4 text-gray-600" />}
      </button>
      {isExpanded && (
        <div className="px-4 pb-4 text-sm text-gray-700 font-['DM_Sans'] space-y-2">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ ...props }) => <h4 className="text-base font-semibold text-gray-900 mt-3" {...props} />,
              h2: ({ ...props }) => <h4 className="text-base font-semibold text-gray-900 mt-3" {...props} />,
              h3: ({ ...props }) => <h4 className="text-sm font-semibold text-gray-900 mt-3" {...props} />,
              p: ({ ...props }) => <p className="text-gray-700" {...props} />,
              ul: ({ ...props }) => <ul className="list-disc pl-5 space-y-1" {...props} />,
              li: ({ ...props }) => <li className="text-gray-700" {...props} />,
              a: ({ href, ...props }) => (
                <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#468BFF] hover:underline" {...props} />
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

const RunDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [run, setRun] = useState<ResearchRunDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const { isGeneratingPdf, isCopied, generatePdf, copyToClipboard } = useReportActions((message) =>
    setError(message)
  );

  useEffect(() => {
    if (!id) return;
    let active = true;

    async function load() {
      setIsLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const detail = await fetchRun(id as string);
        if (active) setRun(detail);
      } catch (err) {
        console.error('Failed to load research run:', err);
        if (active) {
          if (err instanceof Error && err.message.includes('404')) {
            setNotFound(true);
          } else {
            setError('Falha ao carregar a pesquisa.');
          }
        }
      } finally {
        if (active) setIsLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [id]);

  if (isLoading) {
    return (
      <PageShell>
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-[#468BFF]" />
        </div>
      </PageShell>
    );
  }

  if (notFound) {
    return (
      <PageShell>
        <div className={`${glassStyle.card} text-center font-['DM_Sans']`}>
          <p className="text-gray-900 font-medium">Pesquisa não encontrada</p>
          <Link to="/history" className="mt-3 inline-block text-sm font-medium text-[#468BFF] hover:underline">
            Voltar ao histórico
          </Link>
        </div>
      </PageShell>
    );
  }

  if (error || !run) {
    return (
      <PageShell>
        <div className={`${glassStyle.card} border-[#FE363B]/30 bg-[#FE363B]/10 font-['DM_Sans']`}>
          <p className="text-[#FE363B]">{error || 'Falha ao carregar a pesquisa.'}</p>
        </div>
      </PageShell>
    );
  }

  const statusLabel = RUN_HISTORY_STATUS_LABELS[run.status] || run.status;

  return (
    <PageShell>
      <div className="font-['DM_Sans'] space-y-2">
        <Link to="/history" className="text-sm text-[#468BFF] hover:underline">
          Voltar ao histórico
        </Link>
        <h2 className="text-2xl font-semibold text-gray-900">{run.company}</h2>
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
          <span>{statusLabel}</span>
          <span>{formatDateTime(run.created_at)}</span>
          {run.industry && <span>{run.industry}</span>}
          {run.hq_location && <span>{run.hq_location}</span>}
          {run.company_url && (
            <a href={run.company_url} target="_blank" rel="noopener noreferrer" className="text-[#468BFF] hover:underline">
              {run.company_url}
            </a>
          )}
        </div>
      </div>

      {run.status === 'failed' && run.error_message && (
        <div className={`${glassStyle.card} border-[#FE363B]/30 bg-[#FE363B]/10 font-['DM_Sans']`}>
          <p className="text-[#FE363B]">{run.error_message}</p>
        </div>
      )}

      {run.report && (
        <ResearchReport
          output={{ summary: '', details: { report: run.report } }}
          isResetting={false}
          isStreaming={false}
          glassStyle={glassStyle}
          fadeInAnimation={fadeInAnimation}
          loaderColor="#468BFF"
          isGeneratingPdf={isGeneratingPdf}
          isCopied={isCopied}
          onCopyToClipboard={() => copyToClipboard(run.report || '')}
          onGeneratePdf={() => generatePdf(run.report || '', run.company)}
        />
      )}

      {run.briefings.length > 0 && (
        <div className={`${glassStyle.card} font-['DM_Sans']`}>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumos</h3>
          <div className="space-y-3">
            {run.briefings.map((briefing) => (
              <BriefingItem key={briefing.category} category={briefing.category} content={briefing.content} />
            ))}
          </div>
        </div>
      )}

      {run.references.length > 0 && (
        <div className={`${glassStyle.card} font-['DM_Sans']`}>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Referências</h3>
          <ul className="space-y-2">
            {run.references.map((reference) => (
              <li key={reference.position} className="text-sm">
                <a
                  href={reference.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#468BFF] hover:underline"
                >
                  {reference.title || reference.url}
                </a>
                <span className="text-gray-500 ml-2">
                  {reference.domain}
                  {reference.score !== null ? ` · ${reference.score.toFixed(2)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </PageShell>
  );
};

export default RunDetailPage;
